// Volunteer Event Types and Interfaces (Admin.API serializes enums as numbers)

import { NZ_REGIONS } from './TALocal';

export { NZ_REGIONS };

export enum VolunteerEventFormat {
  ScheduledCrew = 0,
  SelfServe = 1,
  Application = 2,
}

export enum VolunteerEventType {
  SingleDay = 0,
  WeekendBasecamp = 1,
  Backpack = 2,
  RapidResponse = 3,
  Multiday = 4,
}

export enum VolunteerEventDifficulty {
  Easy = 0,
  Moderate = 1,
  Hard = 2,
  Strenuous = 3,
}

export enum VolunteerEventStatus {
  Draft = 0,
  Published = 1,
  Cancelled = 2,
  Completed = 3,
}

export enum VolunteerRegistrationStatus {
  Confirmed = 0,
  Waitlisted = 1,
  Cancelled = 2,
}

export const EVENT_FORMAT_LABELS: Record<VolunteerEventFormat, string> = {
  [VolunteerEventFormat.ScheduledCrew]: 'Scheduled crew',
  [VolunteerEventFormat.SelfServe]: 'Self-serve (anytime)',
  [VolunteerEventFormat.Application]: 'Application role',
};

export const EVENT_TYPE_LABELS: Record<VolunteerEventType, string> = {
  [VolunteerEventType.SingleDay]: 'Single day',
  [VolunteerEventType.WeekendBasecamp]: 'Weekend basecamp',
  [VolunteerEventType.Backpack]: 'Backpack',
  [VolunteerEventType.RapidResponse]: 'Rapid response',
  [VolunteerEventType.Multiday]: 'Multiday',
};

export const DIFFICULTY_LABELS: Record<VolunteerEventDifficulty, string> = {
  [VolunteerEventDifficulty.Easy]: 'Easy',
  [VolunteerEventDifficulty.Moderate]: 'Moderate',
  [VolunteerEventDifficulty.Hard]: 'Hard',
  [VolunteerEventDifficulty.Strenuous]: 'Strenuous',
};

export const EVENT_STATUS_LABELS: Record<VolunteerEventStatus, string> = {
  [VolunteerEventStatus.Draft]: 'Draft',
  [VolunteerEventStatus.Published]: 'Published',
  [VolunteerEventStatus.Cancelled]: 'Cancelled',
  [VolunteerEventStatus.Completed]: 'Completed',
};

export const REGISTRATION_STATUS_LABELS: Record<VolunteerRegistrationStatus, string> = {
  [VolunteerRegistrationStatus.Confirmed]: 'Confirmed',
  [VolunteerRegistrationStatus.Waitlisted]: 'Waitlisted',
  [VolunteerRegistrationStatus.Cancelled]: 'Cancelled',
};

// Mirrors TheTrailApp.Core.Enums.VolunteerActivityAction (pinned ints).
export enum VolunteerActivityAction {
  Registered = 0,
  Waitlisted = 1,
  PromotedFromWaitlist = 2,
  CancelledBySelf = 3,
  RemovedByAdmin = 4,
  CapacityChanged = 5,
  EventPublished = 6,
  EventCancelled = 7,
  EventDeleted = 8,
}

export enum VolunteerActorType {
  Volunteer = 0,
  Admin = 1,
  System = 2,
}

export const ACTIVITY_ACTION_LABELS: Record<VolunteerActivityAction, string> = {
  [VolunteerActivityAction.Registered]: 'Registered (confirmed)',
  [VolunteerActivityAction.Waitlisted]: 'Joined the waitlist',
  [VolunteerActivityAction.PromotedFromWaitlist]: 'Promoted from waitlist',
  [VolunteerActivityAction.CancelledBySelf]: 'Cancelled their registration',
  [VolunteerActivityAction.RemovedByAdmin]: 'Removed by admin',
  [VolunteerActivityAction.CapacityChanged]: 'Capacity changed',
  [VolunteerActivityAction.EventPublished]: 'Event published',
  [VolunteerActivityAction.EventCancelled]: 'Event cancelled',
  [VolunteerActivityAction.EventDeleted]: 'Event deleted',
};

export interface VolunteerEventDto {
  id: string;
  title: string;
  summary?: string;
  description?: string;
  format: VolunteerEventFormat;
  eventType: VolunteerEventType;
  difficulty: VolunteerEventDifficulty;
  status: VolunteerEventStatus;
  /** null for non-crew (anytime) formats. */
  startDate: string | null;
  endDate: string | null;
  region: string;
  /** null when the format has no capacity cap (self-serve / application). */
  capacity: number | null;
  meetingLocationDescription?: string;
  latitude?: number;
  longitude?: number;
  leaderName?: string;
  leaderContact?: string;
  whatToBring?: string;
  registrationOpensAt?: string;
  registrationClosesAt?: string;
  imageUrl?: string;
  externalCtaUrl?: string;
  externalCtaLabel?: string;
  confirmedCount: number;
  waitlistCount: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface SaveVolunteerEventRequest {
  title: string;
  summary?: string;
  description?: string;
  format: VolunteerEventFormat;
  eventType: VolunteerEventType;
  difficulty: VolunteerEventDifficulty;
  /** Required for ScheduledCrew only. */
  startDate?: string | null;
  endDate?: string | null;
  region: string;
  /** Required for ScheduledCrew only. */
  capacity?: number | null;
  meetingLocationDescription?: string;
  latitude?: number;
  longitude?: number;
  leaderName?: string;
  leaderContact?: string;
  whatToBring?: string;
  registrationOpensAt?: string;
  registrationClosesAt?: string;
  imageUrl?: string;
  /** External CTA — safety form (self-serve) or apply target (application). */
  externalCtaUrl?: string;
  externalCtaLabel?: string;
}

export interface VolunteerEventListResponse {
  events: VolunteerEventDto[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface RosterEntryDto {
  registrationId: string;
  /** Snapshot taken at registration, falling back to the live profile. */
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  phone?: string;
  status: VolunteerRegistrationStatus;
  waitlistPosition?: number;
  registeredAt: string;
  promotedAt?: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  medicalOrDietaryNotes?: string;
  /** Only on cancelled rows (roster fetched with includeCancelled). */
  cancelledAt?: string;
  cancelledBy?: string;
  cancellationReason?: string;
}

export interface RosterResponse {
  roster: RosterEntryDto[];
}

export interface RemoveRegistrantResponse {
  registrationId: string;
  promotedRegistrationId?: string;
  confirmedCount: number;
  waitlistCount: number;
}

export interface VolunteerActivityEntryDto {
  id: number;
  action: VolunteerActivityAction;
  actorType: VolunteerActorType;
  /** Admin identity for admin actions; absent for volunteer/system entries. */
  actorName?: string;
  registrationId?: string;
  subjectName?: string;
  subjectEmail?: string;
  occurredAt: string;
  /** JSON string with action-specific context (reason, previousCapacity/newCapacity, …). */
  details?: string;
}

export interface ActivityResponse {
  activity: VolunteerActivityEntryDto[];
}
